import { Node, Link, Rectangle } from 'webcola';
import { InstanceLayout, LayoutNode, LayoutEdge, LayoutConstraint, LayoutGroup, isLeftConstraint, isTopConstraint, isAlignmentConstraint, isBoundingBoxConstraint, isGroupBoundaryConstraint, GroupBoundaryConstraint, ColorSource } from '../../layout/interfaces';
import { EdgeStyle } from '../../layout/edge-style';
import type { TextStyle } from '../../layout/style/text-style';
import type { IconPlacement } from '../../layout/style/atom-style-spec';
import type { IDataInstance } from '../../data-instance/interfaces';
import type { SequencePolicy } from './sequence-policy';
import { computeConstraintAwareSeed, hasSeedableConstraints, SeedPosition } from './constraint-aware-seed';
import * as dagre from 'dagre';

/**
 * WebColaTranslator - Translates InstanceLayout to WebCola format
 * 
 * WebCola is a constraint-based layout library that allows for precise 
 * positioning of nodes using separation and alignment constraints.
 */




/**
 * WebColaTranslator - Translates InstanceLayout to WebCola format
 * 
 * WebCola is a constraint-based layout library that allows for precise 
 * positioning of nodes using separation and alignment constraints.
 */

/**
 * Extra padding added to each side of a node's collision bounds for WebCola's
 * overlap-avoidance engine. The visual (rendered) size remains the original node size.
 */
const NODE_LAYOUT_PADDING = 8;

/**
 * Disconnected nodes (no edges) get more padding so they sit clearly apart from
 * the main graph cluster rather than being placed flush against it.
 */
const NODE_LAYOUT_PADDING_DISCONNECTED = 20;

type NodeWithMetadata = Node & {

  label: string, // This is the label that will be displayed on the node
  id: string,
  attributes: Record<string, string[]>,
  /** Per-attribute-key text style (size + color); drives the font size and fill of each attribute line. */
  attributeTextStyles?: Record<string, TextStyle>,
  /**
   * Labels from the data instance (e.g., Skolems in Alloy).
   * These are displayed prominently on nodes, styled in the node's color.
   */
  labels?: Record<string, string[]>,
  color: string,
  /** Provenance of `color` (default palette vs. user directive). See {@link ColorSource}. */
  colorSource?: ColorSource,
  /** Interior fill from `atomStyle.fillStyle.color`; absent = renderer's canvas-matched default. */
  fillColor?: string,
  /** Border/stroke width in px from `atomStyle.borderStyle.width`; absent = renderer default. */
  borderWidth?: number,
  /** Main-label styling from `atomStyle.textStyle` (only `color` consumed today; see LayoutNode). */
  textStyle?: TextStyle,
  icon: string,
  /** Icon geometry from `atomStyle.iconStyle.placement`: 'full' | 'badge'. */
  iconPlacement?: IconPlacement,
  /** Icon alpha from `atomStyle.iconStyle.opacity`; absent = opaque. */
  iconOpacity?: number,
  mostSpecificType: string,
  showLabels: boolean,
  /**
   * Collision rectangle in layout space. WebCola sets this when `avoidOverlaps`
   * is on, and `ensureNodeBounds` recomputes it from the VISUAL size so routing
   * and hit-testing use the rendered box rather than the padded collision box.
   * Absent until the layout's first tick has run.
   */
  bounds?: Rectangle,
  /** {@link bounds} inflated by -1 — WebCola's inner rectangle, used for routing. */
  innerBounds?: Rectangle,
  /** Original visual width of the node (without layout padding). Use this for rendering. */
  visualWidth: number,
  /** Original visual height of the node (without layout padding). Use this for rendering. */
  visualHeight: number,
};

/**
 * A WebCola link carrying our rendering metadata.
 *
 * Endpoints are typed as NODES, not indices. WebCola links are BUILT with node
 * indices, but the layout swaps each index for the node object in place on its
 * first tick, and every downstream reader (rendering, routing, hit-testing,
 * label placement) sees a node. Construction sites funnel their index through
 * {@link edgeEndpointFromIndex}, which is the one documented place the two
 * representations meet.
 *
 * (This used to be declared `Link<NodeWithMetadata> & { source: number }`, which
 * asked for the impossible type `NodeWithMetadata & number` — reads compiled,
 * every write was an error.)
 */
type EdgeWithMetadata = Link<NodeWithMetadata> & {
  relName: string, // This is the name of the relation for the edge
  id: string, // Unique identifier for the edge
  label: string, // This is what is displayed on the edge
  color: string,
  style?: EdgeStyle,
  weight?: number,
  /** Highlight color rendered as a wider underlay beneath the edge. Undefined = no highlight. */
  highlight?: string,
  showLabel?: boolean, // Whether to show the edge label (default: true)
  /** Optional label styling (size / color) for the edge's label text. */
  textStyle?: TextStyle,
  bidirectional?: boolean, // Flag to indicate if this edge represents a bidirectional relationship
  /** Provenance: set when the user drew this edge in input mode, rather than it coming from the data instance. */
  isUserCreated?: boolean,
  /**
   * For group edges (id starts with `_g_`), the name of the group this edge
   * was created for (matches `group.id`). INFORMATIONAL since the per-end
   * stamp unification — routing reads only sourceGroupId/targetGroupId.
   */
  groupId?: string,
  /**
   * For group edges, the node ID of the key (groupOn) node — the external
   * anchor. INFORMATIONAL since the per-end stamp unification: the renderer
   * no longer derives the snap side from it.
   */
  keyNodeId?: string,
  /**
   * The group (matches `group.id`) whose hull the SOURCE end attaches to —
   * the renderer's single group-edge contract, set by inferredEdge `draw`
   * and by the `addEdge` connector desugar ('fromgroup' stamps this side);
   * the source node is only the anchor. Undefined = the source end renders
   * at the node itself.
   */
  sourceGroupId?: string,
  /** Same as {@link sourceGroupId}, for the TARGET end ('togroup' stamps it). */
  targetGroupId?: string,
};

// Export the types for use in other modules
export type { NodeWithMetadata, EdgeWithMetadata };

/**
 * Bridge an edge endpoint from the index form WebCola is fed to the node form
 * WebCola hands back. See {@link EdgeWithMetadata} for why the endpoint types
 * describe the post-layout shape.
 *
 * ONLY for links being built for a layout that has NOT started yet.
 *
 * WebCola swaps indices for node objects in exactly one place —
 * `Layout.start()`. Nothing resolves them afterwards. So an index handed to a
 * layout that is already running stays a number forever while its type claims
 * to be a node, and every reader (routing, rendering, hit-testing) misreads it.
 * To attach an edge to a LIVE layout, store the node itself
 * (`currentLayout.nodes[i]`) rather than calling this.
 */
export function edgeEndpointFromIndex(index: number): NodeWithMetadata {
  return index as unknown as NodeWithMetadata;
}

/**
 * Read an edge endpoint back as a node index, accepting either representation:
 * the raw index before WebCola's first tick, or the resolved node's `index`
 * after it. Returns -1 when the endpoint carries no index.
 */
export function edgeEndpointToIndex(endpoint: NodeWithMetadata | number): number {
  return typeof endpoint === 'number' ? endpoint : endpoint?.index ?? -1;
}

/**
 * Position hint for a node, used to initialize node positions for temporal consistency.
 * When rendering temporal sequences, nodes should start at their previous positions
 * to maintain visual stability across frames.
 * 
 * WebCola uses these positions as initial values when starting the layout algorithm.
 * The positions are respected during the constraint-solving phase, with reduced
 * unconstrained iterations to better preserve them.
 */
export interface NodePositionHint {
  /** Node identifier - matched by id */
  id: string;
  /** X coordinate from previous render */
  x: number;
  /** Y coordinate from previous render */
  y: number;
}

/**
 * Transform information representing zoom/pan state.
 * Used to normalize positions when the viewbox/zoom has changed between renders.
 */
export interface TransformInfo {
  /** Scale factor (zoom level) */
  k: number;
  /** X translation (pan offset) */
  x: number;
  /** Y translation (pan offset) */
  y: number;
}

/**
 * Complete layout state snapshot that can be captured and restored.
 * Bundles node positions with the zoom/pan transform.
 *
 * Use `graph.getLayoutState()` to capture, then pass as `priorPositions`
 * in `WebColaLayoutOptions`.
 */
export interface LayoutState {
  /** Node positions from the layout */
  positions: NodePositionHint[];
  /** Zoom/pan transform at time of capture */
  transform: TransformInfo;
}

/**
 * Rendering transition mode used when applying a new layout.
 * - `replace`: clear previous frame immediately (legacy behavior)
 * - `morph`: animate individual elements — exiting nodes/edges fade out,
 *   entering ones fade in, and continuing ones slide to new positions.
 */
export type WebColaRenderTransitionMode = 'replace' | 'morph';

/**
 * Options for `renderLayout`.
 *
 * There are two modes:
 *
 * 1. **Fresh layout** (default) — omit all fields.
 * 2. **Policy-driven** — pass `policy`, `prevInstance`, and `currInstance`.
 *    The graph captures its own layout state (or uses an explicit
 *    `priorPositions` if provided) and lets the policy decide what
 *    reaches the solver.
 *
 * @example
 * ```typescript
 * // Fresh
 * await graph.renderLayout(layout);
 *
 * // Policy-driven (graph captures state automatically)
 * await graph.renderLayout(nextLayout, {
 *   policy: stability,
 *   prevInstance: instanceA,
 *   currInstance: instanceB,
 * });
 *
 * // Policy-driven with explicit prior positions
 * await graph.renderLayout(nextLayout, {
 *   policy: stability,
 *   prevInstance: instanceA,
 *   currInstance: instanceB,
 *   priorPositions: graph.getLayoutState(),
 *   transitionMode: 'morph',
 * });
 * ```
 */
export interface WebColaLayoutOptions {
  /**
   * A sequence policy that transforms prior state before it reaches the
   * solver.  Requires `prevInstance` and `currInstance`.
   */
  policy?: SequencePolicy;

  /** The data instance rendered in the *previous* step. */
  prevInstance?: IDataInstance;

  /** The data instance being rendered *now*. */
  currInstance?: IDataInstance;

  /**
   * Explicit prior layout state to feed into the policy.
   *
   * If omitted the graph calls `getLayoutState()` internally, which
   * includes any drag the user may have performed.
   */
  priorPositions?: LayoutState;

  /**
   * Optional transition behavior when swapping frames.
   * If omitted, the element-level `transition-mode` attribute is used.
   */
  transitionMode?: WebColaRenderTransitionMode;

  /**
   * When false, opposite-direction tuples with the same label (A→B and B→A)
   * are NOT merged into a single double-headed edge. The editable
   * StructuredInputGraph turns collapse OFF so each tuple stays an independent,
   * individually-editable arrow; the read-only base leaves it ON for a tidy
   * double-header. Defaults to true.
   */
  collapseSymmetricEdges?: boolean;

  /**
   * When true (stability mode), nodes with prior positions are locked
   * at those positions (fixed=1) wherever it does not violate a
   * constraint:
   *
   *   - Unconstrained nodes with prior positions stay locked.
   *   - Constrained nodes whose prior positions already SATISFY the
   *     constraint stay locked. WebCola treats fixed=1 as a strong
   *     soft anchor (default weight 1000 in the VPSC projector and a
   *     max-Hessian spring in gradient descent), so the solver will
   *     still keep the constraint satisfied if anything else moves.
   *   - Constrained nodes whose prior positions VIOLATE the constraint
   *     are unfixed so the solver can move them freely.
   *   - New nodes (no prior position) are unfixed and seeded via DAGRE
   *     or the figure center.
   *
   * When false (default / morph mode), every node involved in any
   * constraint is unfixed — the legacy behavior that lets gradient
   * descent and alignment-edge spring forces freely re-place them.
   */
  lockUnconstrainedNodes?: boolean;

  /**
   * Which seeding strategy computes initial node positions when no prior
   * positions exist (issue #427).
   *
   *   - 'constraint-aware' (default): solve the spec's separation/alignment
   *     constraints exactly and spend the free coordinates on displayed
   *     symmetry (see constraint-aware-seed.ts). Start positions only —
   *     no locks, no extra links or constraints; the solver runs unchanged
   *     and remains free to move every node. Falls back to DAGRE when
   *     there are no seedable constraints or the system is cyclic.
   *   - 'dagre': the legacy edges-only DAGRE seed.
   *
   * Dev A/B fallback: when no explicit option is passed,
   * `globalThis.SPYTIAL_SEED_MODE` selects the mode (for live comparison
   * in demo consoles).
   */
  seedMode?: 'constraint-aware' | 'dagre';
}

// WebCola constraint types
export interface ColaConstraint {
  type: string;
  [key: string]: unknown;
}

export interface ColaSeparationConstraint extends ColaConstraint {
  type: 'separation';
  axis: 'x' | 'y';
  left: number;
  right: number;
  gap: number;
  equality?: boolean;
}

export interface ColaHierarchyConstraint extends ColaConstraint {
  type: 'hierarchy';
  parent: number;
  child: number;
  gap: number;
}

// // WebCola group definition
// interface ColaGroupDefinition extends Group {
//   leaves?: NodeWithMetadata[];
//   groups?: ColaGroupDefinition[];
//   padding: number;
//   name: string;
//   keyNode?: number;
//   id?: string;
//   showLabel?: boolean;
// }


export class WebColaLayout {

  private instanceLayout: InstanceLayout;
  readonly colaConstraints: ColaConstraint[];
  readonly colaNodes: NodeWithMetadata[];
  readonly colaEdges: EdgeWithMetadata[];
  readonly groupDefinitions: any;
  readonly conflictingConstraints: LayoutConstraint[];
  readonly overlappingNodesData: LayoutNode[];
  readonly reintroducedNodesData: LayoutNode[];

  private readonly DEFAULT_X: number;
  private readonly DEFAULT_Y: number;

  private dagre_graph: any;

  public FIG_WIDTH: number;
  public FIG_HEIGHT: number;

  /**
   * Map of node id to prior position hint.
   * Used to initialize nodes at their previous positions for temporal consistency.
   */
  private priorPositionMap: Map<string, NodePositionHint>;

  /**
   * Map of node id to its index in `colaNodes`. Built once after `colaNodes`
   * is constructed. Backs `getNodeIndex()` so constraint translation and
   * the post-pass run in O(c) instead of O(c × n).
   */
  private nodeIndexMap: Map<string, number> = new Map();

  /** When true, lock unconstrained nodes with prior positions via fixed=1. */
  private lockUnconstrainedNodes: boolean;
  private collapseSymmetric: boolean;

  /**
   * Constraint-aware start positions (issue #427). Non-null only when
   * there are no prior positions, seedMode allows it, and the constraint
   * system was solvable. Consulted by toColaNode() ahead of the DAGRE
   * seed. Start positions only — never adds locks, links, or constraints.
   */
  private seedPositions: Map<string, SeedPosition> | null = null;

  constructor(instanceLayout: InstanceLayout, fig_height: number = 800, fig_width: number = 800, options?: WebColaLayoutOptions) {

    this.FIG_HEIGHT = fig_height;
    this.FIG_WIDTH = fig_width;

    this.DEFAULT_X = fig_width / 2;
    this.DEFAULT_Y = fig_height / 2;

    this.instanceLayout = instanceLayout;

    // Build a map of prior positions for O(1) lookup
    this.priorPositionMap = new Map();

    // The caller (renderLayout) resolves the policy and passes the
    // effective positions through priorPositions.
    if (options?.priorPositions?.positions) {
      for (const hint of options.priorPositions.positions) {
        this.priorPositionMap.set(hint.id, hint);
      }
    }

    this.lockUnconstrainedNodes = options?.lockUnconstrainedNodes ?? false;
    this.collapseSymmetric = options?.collapseSymmetricEdges ?? true;

    // Can I create a DAGRE graph here.
    try {
      const g = new dagre.graphlib.Graph({ multigraph: true });
      g.setGraph({ nodesep: 50, ranksep: 100, rankdir: 'TB' });
      g.setDefaultEdgeLabel(() => ({}));

      instanceLayout.nodes.forEach(node => {
        g.setNode(node.id, { width: node.width, height: node.height });
      });

      instanceLayout.edges.forEach(edge => {
        g.setEdge(edge.source.id, edge.target.id);
      });
      dagre.layout(g);

      this.dagre_graph = g;
    }
    catch (e) {
      console.log(e);
      this.dagre_graph = null;
    }

    // Constraint-aware start positions (issue #427). Only engages on a
    // fresh layout (no priors) with at least one seedable constraint;
    // returns null on cyclic/contradictory systems, leaving the DAGRE seed.
    //
    // These are START POSITIONS ONLY — no locks, links, or constraints are
    // added, so the solver is exactly as free as before; a bad seed costs
    // nothing beyond what DAGRE cost. What a good seed buys: the solve
    // starts with every separation/alignment constraint already satisfied
    // (constraint-projection phases begin as near-no-ops), the starting
    // basin is deterministic and structure-respecting, and on large graphs
    // — where renderLayout caps iterations and the solve does NOT fully
    // re-converge — the seed genuinely shapes the final layout. On small
    // graphs the run-to-convergence solve is seed-independent (measured in
    // tests/seed-experiment.test.ts); that is expected and fine.
    const seedMode =
      options?.seedMode ?? (globalThis as any)?.SPYTIAL_SEED_MODE ?? 'constraint-aware';
    if (
      seedMode !== 'dagre' &&
      this.priorPositionMap.size === 0 &&
      hasSeedableConstraints(instanceLayout)
    ) {
      try {
        this.seedPositions = computeConstraintAwareSeed(instanceLayout, fig_width, fig_height);
      } catch (e) {
        console.warn('Constraint-aware seed failed, falling back to DAGRE:', e);
        this.seedPositions = null;
      }
    }

    this.colaNodes = instanceLayout.nodes.map(node => this.toColaNode(node));
    // Build node-id -> index map. Used by getNodeIndex() to keep
    // constraint translation and the constraint-aware locking post-pass
    // O(c) instead of O(c × n).
    for (let i = 0; i < this.colaNodes.length; i++) {
      this.nodeIndexMap.set(this.colaNodes[i].id, i);
    }
    this.colaEdges = instanceLayout.edges.map(edge => this.toColaEdge(edge));

    // Collapse symmetric edges with the same label into one double-headed edge.
    // Editable graphs disable this so each direction stays an independent,
    // individually-editable arrow (see collapseSymmetricEdges option).
    if (this.collapseSymmetric) {
      this.colaEdges = this.collapseSymmetricEdges(this.colaEdges);
    }

    // Collapse identical nested groups to reduce jitter and constraint conflicts
    const deduplicatedGroups = this.collapseIdenticalGroups(instanceLayout.groups);
    this.groupDefinitions = this.determineGroups(deduplicatedGroups);

    // The collapse renames merged groups, which would orphan edge fields
    // stamped with pre-merge group names. Remap them to the merged names
    // (merged groups have identical members, hence identical hulls).
    if (this.collapsedGroupAliases.size > 0) {
      for (const e of this.colaEdges) {
        if (e.groupId) e.groupId = this.collapsedGroupAliases.get(e.groupId) ?? e.groupId;
        if (e.sourceGroupId) e.sourceGroupId = this.collapsedGroupAliases.get(e.sourceGroupId) ?? e.sourceGroupId;
        if (e.targetGroupId) e.targetGroupId = this.collapsedGroupAliases.get(e.targetGroupId) ?? e.targetGroupId;
      }
    }

    this.conflictingConstraints = instanceLayout.conflictingConstraints || [];
    this.overlappingNodesData = instanceLayout.overlappingNodes || [];
    this.reintroducedNodesData = instanceLayout.reintroducedNodes || [];
    this.colaConstraints = instanceLayout.constraints.flatMap(constraint => this.toColaConstraint(constraint));

    // Decide which constraint endpoints stay locked (fixed=1) and which
    // get unfixed. In stability mode (lockUnconstrainedNodes=true) we
    // only unfix endpoints whose prior positions would violate the
    // constraint. Outside stability mode this preserves the historical
    // behavior of unfixing every constrained node.
    this.applyConstraintAwareLocking(this.colaConstraints);

    // Apply transitive reduction optimization if we have many constraints
    // Threshold: optimize when we have more than 100 constraints
    const originalConstraintCount = this.colaConstraints.length;
    const OPTIMIZATION_THRESHOLD = 100;
    if (originalConstraintCount > OPTIMIZATION_THRESHOLD) {
      this.colaConstraints = this.optimizeConstraints(this.colaConstraints);
    }

    if (this.colaConstraints.length === 0 && this.dagre_graph) {
      this.colaNodes.forEach(node => node.fixed = 1);
    }


  }

  /**
   * Optimizes constraints by applying transitive reduction to remove redundant constraints.
   * 
   * Graph Theory Approach:
   * - Separation constraints (left/right, up/down) form directed acyclic graphs (DAGs)
   * - Transitive reduction removes edges (constraints) that are implied by other paths
   * - Example: If A left-of B, B left-of C, and A left-of C, then A left-of C is redundant
   * 
   * This optimization is crucial for large graphs where constraint counts can explode:
   * - Reduces solver complexity from O(n³) to closer to O(n²) or O(n log n)
   * - Prevents WebCola performance degradation with many constraints
   * - Maintains layout correctness since redundant constraints don't add information
   * 
   * @param constraints Array of WebCola constraints to optimize
   * @returns Optimized array with redundant constraints removed
   */
  private optimizeConstraints(constraints: ColaConstraint[]): ColaConstraint[] {
    // Separate constraints by type for independent optimization
    const xSeparationConstraints: ColaSeparationConstraint[] = [];
    const ySeparationConstraints: ColaSeparationConstraint[] = [];
    const otherConstraints: ColaConstraint[] = [];

    for (const constraint of constraints) {
      if (constraint.type === 'separation' && !constraint.groupBoundary) {
        const sepConstraint = constraint as ColaSeparationConstraint;
        if (sepConstraint.axis === 'x' && !sepConstraint.equality) {
          xSeparationConstraints.push(sepConstraint);
        } else if (sepConstraint.axis === 'y' && !sepConstraint.equality) {
          ySeparationConstraints.push(sepConstraint);
        } else {
          // Alignment constraints (equality: true) should not be reduced
          otherConstraints.push(constraint);
        }
      } else {
        otherConstraints.push(constraint);
      }
    }

    // Apply transitive reduction to each axis independently
    const optimizedX = this.transitiveReductionForSeparation(xSeparationConstraints);
    const optimizedY = this.transitiveReductionForSeparation(ySeparationConstraints);

    // Combine optimized constraints
    return [...optimizedX, ...optimizedY, ...otherConstraints];
  }

  /**
   * Performs transitive reduction on separation constraints using Floyd-Warshall-inspired approach.
   * 
   * Algorithm:
   * 1. Build a reachability matrix using the direct constraints
   * 2. Compute transitive closure (all implied relationships)
   * 3. Keep only constraints that are not implied by other paths
   * 
   * Time Complexity: O(n³) where n is the number of nodes
   * Space Complexity: O(n²) for the reachability matrix
   * 
   * This is acceptable because:
   * - Only runs when constraint count is high (threshold-based)
   * - The cost is amortized across the entire layout process
   * - The reduction in constraints saves much more time in WebCola solver
   * 
   * @param constraints Array of separation constraints to optimize
   * @returns Optimized array with redundant constraints removed
   */
  private transitiveReductionForSeparation(constraints: ColaSeparationConstraint[]): ColaSeparationConstraint[] {
    if (constraints.length === 0) {
      return constraints;
    }

    const n = this.colaNodes.length;
    
    // Build adjacency matrix for direct constraints
    // direct[i][j] stores the constraint if there's a direct edge from i to j
    const direct: (ColaSeparationConstraint | null)[][] = Array(n).fill(null).map(() => Array(n).fill(null));
    
    for (const constraint of constraints) {
      // Validate indices to prevent prototype pollution
      // The indices come from constraint objects but we validate they are:
      // 1. Actually numbers (not strings like '__proto__')
      // 2. Integers (not floating point)
      // 3. Within valid array bounds
      // This prevents any prototype pollution attacks
      const left = constraint.left;
      const right = constraint.right;
      
      // Ensure indices are valid numbers within bounds
      if (typeof left === 'number' && typeof right === 'number' && 
          left >= 0 && left < n && right >= 0 && right < n &&
          Number.isInteger(left) && Number.isInteger(right)) {
        direct[left][right] = constraint;
      }
    }

    // Compute transitive closure using Floyd-Warshall
    // reachable[i][j] is true if j is reachable from i through any path
    const reachable: boolean[][] = Array(n).fill(false).map(() => Array(n).fill(false));
    
    // Initialize: direct edges are reachable
    for (let i = 0; i < n; i++) {
      for (let j = 0; j < n; j++) {
        reachable[i][j] = direct[i][j] !== null;
      }
    }

    // Floyd-Warshall: if i->k and k->j, then i->j is reachable
    for (let k = 0; k < n; k++) {
      for (let i = 0; i < n; i++) {
        for (let j = 0; j < n; j++) {
          if (reachable[i][k] && reachable[k][j]) {
            reachable[i][j] = true;
          }
        }
      }
    }

    // Keep only constraints that are not redundant
    // A constraint i->j is redundant if there exists an intermediate node k
    // such that i->k and k->j both exist (or are reachable)
    const nonRedundant: ColaSeparationConstraint[] = [];
    
    for (const constraint of constraints) {
      const i = constraint.left;
      const j = constraint.right;
      let isRedundant = false;

      // Check if there's an alternative path from i to j through any intermediate node k
      for (let k = 0; k < n; k++) {
        if (k !== i && k !== j) {
          // If both i->k and k->j exist as direct constraints, then i->j is redundant
          if (direct[i][k] !== null && direct[k][j] !== null) {
            isRedundant = true;
            break;
          }
        }
      }

      if (!isRedundant) {
        nonRedundant.push(constraint);
      }
    }

    return nonRedundant;
  }

  private getNodeIndex(nodeId: string): number {
    const idx = this.nodeIndexMap.get(nodeId);
    return idx === undefined ? -1 : idx;
  }

  /**
   * Computes adaptive horizontal separation between two nodes using their actual dimensions
   */
  private computeHorizontalSeparation(node1: NodeWithMetadata, node2: NodeWithMetadata, minDistance: number): number {
    // Use actual visual node widths (not the inflated collision bounds)
    const node1Width = node1.visualWidth ?? node1.width ?? 100;
    const node2Width = node2.visualWidth ?? node2.width ?? 100;
    
    // Base separation: half-widths + minimum distance
    const baseSeparation = (node1Width / 2) + (node2Width / 2) + minDistance;
    
    // Add adaptive padding based on larger nodes (they likely have more content)
    const maxWidth = Math.max(node1Width, node2Width);
    const adaptivePadding = Math.min(maxWidth * 0.1, 20); // up to 20px extra for large nodes
    
    return baseSeparation + adaptivePadding;
  }

  /**
   * Computes adaptive vertical separation between two nodes using their actual dimensions
   */
  private computeVerticalSeparation(node1: NodeWithMetadata, node2: NodeWithMetadata, minDistance: number): number {
    // Use actual visual node heights (not the inflated collision bounds)
    const node1Height = node1.visualHeight ?? node1.height ?? 60;
    const node2Height = node2.visualHeight ?? node2.height ?? 60;
    
    // Base separation: half-heights + minimum distance
    const baseSeparation = (node1Height / 2) + (node2Height / 2) + minDistance;
    
    // Add adaptive padding based on larger nodes (they likely have more content)
    const maxHeight = Math.max(node1Height, node2Height);
    const adaptivePadding = Math.min(maxHeight * 0.1, 15); // up to 15px extra for tall nodes
    
    return baseSeparation + adaptivePadding;
  }



  private leftConstraint(leftNode: number, rightNode: number, sep: number) {
    // Define a separation constraint to place node A to the left of node B




    const separationConstraint = {
      type: "separation",
      axis: 'x',
      left: leftNode,
      right: rightNode,
      gap: sep,
    };
    return separationConstraint;
  }


  private topConstraint(topNode: number, bottomNode: number, sep: number) {
    // Define a separation constraint to place node A above node B
    const separationConstraint = {
      type: "separation",
      axis: 'y',
      left: topNode,
      right: bottomNode,
      gap: sep,
    };
    return separationConstraint;
  }

  private heirarchyConstraint(parentNodeIndex: number, childNodeIndex: number, sep: number) {

    const heirarchyConstraint = {
      type: 'hierarchy',
      parent: parentNodeIndex,
      child: childNodeIndex,
      gap: sep,
    };
    return heirarchyConstraint;
  }




  /**
   * Converts a LayoutNode to a NodeWithMetadata for WebCola.
   * 
   * Position initialization priority:
   * 1. Prior positions (if available via WebColaLayoutOptions.priorPositions)
   * 2. DAGRE-computed positions (if DAGRE graph is available)
   * 3. Default center position (DEFAULT_X, DEFAULT_Y)
   * 
   * When `lockUnconstrainedNodes` is true (stability mode), nodes with
   * prior positions are initially locked (fixed=1). After all
   * constraints are translated, `applyConstraintAwareLocking()` runs
   * a post-pass that unfixes endpoints whose prior positions violate a
   * constraint (so the solver can repair) and unfixes new (unseeded)
   * endpoints in mixed-prior constraints. Endpoints whose prior
   * positions already satisfy their constraints stay locked.
   * 
   * @param node - The LayoutNode to convert
   * @returns NodeWithMetadata for WebCola
   */
  private toColaNode(node: LayoutNode): NodeWithMetadata {

    let x = this.DEFAULT_X;
    let y = this.DEFAULT_Y;

    let fixed = 0;

    // Priority 1: Use prior position if available (for temporal consistency)
    const priorPosition = this.priorPositionMap.get(node.id);
    if (priorPosition) {
      x = priorPosition.x;
      y = priorPosition.y;
      if (this.lockUnconstrainedNodes) {
        fixed = 1; // Lock at prior position; toColaConstraint() unlocks if constrained
      }
      //console.log(`Node ${node.id}: Using prior position (${x.toFixed(2)}, ${y.toFixed(2)})`);
    } else if (this.priorPositionMap.size > 0) {
      // We have prior positions but this node wasn't found - log for debugging
      console.log(`Node ${node.id}: No prior position found (available: ${Array.from(this.priorPositionMap.keys()).join(', ')})`);
      if (this.dagre_graph) {
        const dagre_node = this.dagre_graph.node(node.id);
        if (dagre_node) {
          x = dagre_node.x;
          y = dagre_node.y;
        }
      }
    } else if (this.seedPositions?.has(node.id)) {
      // Priority 2: Constraint-aware start position (issue #427).
      // Satisfies the spec's separation/alignment constraints exactly and
      // mirrors isomorphic subtrees; start position only — the solver runs
      // unchanged and stays free to move the node.
      const seedPosition = this.seedPositions.get(node.id)!;
      x = seedPosition.x;
      y = seedPosition.y;
    } else if (this.dagre_graph) {
      // Priority 3: Use DAGRE-computed position for new nodes
      const dagre_node = this.dagre_graph.node(node.id);
      if (dagre_node) {
        x = dagre_node.x;
        y = dagre_node.y;
      }
    }


    return {
      id: node.id,
      color: node.color,
      colorSource: node.colorSource ?? ColorSource.DefaultPalette,
      fillColor: node.fillColor,
      borderWidth: node.borderWidth,
      textStyle: node.textStyle,
      attributes: node.attributes || {},
      attributeTextStyles: node.attributeTextStyles || {},
      labels: node.labels,
      // Inflate width/height by padding on each side so WebCola's avoidOverlaps
      // engine keeps a visible gap between nodes. Disconnected nodes get larger
      // padding so they sit clearly apart from the main cluster.
      // The visual size is stored in visualWidth/visualHeight and used for rendering.
      width: node.width + 2 * (node.disconnected ? NODE_LAYOUT_PADDING_DISCONNECTED : NODE_LAYOUT_PADDING),
      height: node.height + 2 * (node.disconnected ? NODE_LAYOUT_PADDING_DISCONNECTED : NODE_LAYOUT_PADDING),
      visualWidth: node.width,
      visualHeight: node.height,
      x: x,
      y: y,
      icon: node.icon || '',
      iconPlacement: node.iconPlacement,
      iconOpacity: node.iconOpacity,
      fixed: fixed,
      mostSpecificType: node.mostSpecificType,
      showLabels: node.showLabels,
      label : node.label
    }
  }

  private toColaEdge(edge: LayoutEdge): EdgeWithMetadata {

    let sourceIndex = this.getNodeIndex(edge.source.id);
    let targetIndex = this.getNodeIndex(edge.target.id);



    return {
      source: edgeEndpointFromIndex(sourceIndex),
      target: edgeEndpointFromIndex(targetIndex),
      relName: edge.relationName,
      id: edge.id,
      label: edge.label,
      color: edge.color,
      style: edge.style,
      weight: edge.weight,
      highlight: edge.highlight,
      showLabel: edge.showLabel,
      textStyle: edge.textStyle,
      groupId: edge.groupId,
      keyNodeId: edge.keyNodeId,
      sourceGroupId: edge.sourceGroupId,
      targetGroupId: edge.targetGroupId,
    }
  }

  /**
   * Collapses symmetric edges with the same label into bidirectional edges.
   * If two nodes have edges between them with the same label (A->B and B->A),
   * they should be collapsed into a single bidirectional edge.
   * Edges with different labels should NOT be collapsed.
   * 
   * @param edges - Array of edges to process
   * @returns Array of edges with symmetric edges collapsed
   */
  private collapseSymmetricEdges(edges: EdgeWithMetadata[]): EdgeWithMetadata[] {
    const edgeMap = new Map<string, EdgeWithMetadata>();
    const processed = new Set<string>();

    for (const edge of edges) {
      // Skip if already processed
      if (processed.has(edge.id)) {
        continue;
      }

      // A self-loop (source === target) is NOT symmetric: it has a single
      // direction and would otherwise match itself as its own reverse below,
      // spuriously rendering a second arrowhead. Keep it as-is.
      if (edge.source === edge.target) {
        edgeMap.set(edge.id, edge);
        processed.add(edge.id);
        continue;
      }

      // Create a key for the edge pair (always use lower source/target index first for consistency)
      const sourceIndex = edgeEndpointToIndex(edge.source);
      const targetIndex = edgeEndpointToIndex(edge.target);
      const minIndex = Math.min(sourceIndex, targetIndex);
      const maxIndex = Math.max(sourceIndex, targetIndex);
      const pairKey = `${minIndex}-${maxIndex}-${edge.label}`;

      // Look for the reverse edge with the same label (never the edge itself)
      const reverseEdge = edges.find(e =>
        e.id !== edge.id &&
        e.source === edge.target &&
        e.target === edge.source &&
        e.label === edge.label &&
        !processed.has(e.id)
      );

      if (reverseEdge) {
        // Found a symmetric pair with the same label - collapse them
        // Keep the edge with the lower source index as the canonical direction
        const canonicalEdge = edge.source < edge.target ? edge : reverseEdge;
        
        edgeMap.set(pairKey, {
          ...canonicalEdge,
          bidirectional: true
        });

        // Mark both edges as processed
        processed.add(edge.id);
        processed.add(reverseEdge.id);
      } else {
        // No matching reverse edge - keep as unidirectional
        edgeMap.set(edge.id, edge);
        processed.add(edge.id);
      }
    }

    return Array.from(edgeMap.values());
  }

  private toColaConstraint(constraint: LayoutConstraint): ColaConstraint | ColaConstraint[] {

    // Pure translation: do NOT mutate node.fixed here. The post-pass in
    // applyConstraintAwareLocking() decides whether to unfix endpoints
    // based on whether their prior positions already satisfy the constraint.

    if (isLeftConstraint(constraint)) {
      const node1 = this.colaNodes[this.getNodeIndex(constraint.left.id)];
      const node2 = this.colaNodes[this.getNodeIndex(constraint.right.id)];
      const distance = this.computeHorizontalSeparation(node1, node2, constraint.minDistance);
      return this.leftConstraint(this.getNodeIndex(constraint.left.id), this.getNodeIndex(constraint.right.id), distance);
    }

    if (isTopConstraint(constraint)) {
      const node1 = this.colaNodes[this.getNodeIndex(constraint.top.id)];
      const node2 = this.colaNodes[this.getNodeIndex(constraint.bottom.id)];
      const distance = this.computeVerticalSeparation(node1, node2, constraint.minDistance);
      return this.topConstraint(this.getNodeIndex(constraint.top.id), this.getNodeIndex(constraint.bottom.id), distance);
    }

    if (isAlignmentConstraint(constraint)) {
      return {
        type: "separation",
        axis: constraint.axis,
        left: this.getNodeIndex(constraint.node1.id),
        right: this.getNodeIndex(constraint.node2.id),
        gap: 0,
        'equality': true
      };
    }

    if(isBoundingBoxConstraint(constraint)) {
      // Log and ignore for now
      console.log("BoundingBoxConstraint detected.");
      // We should return a null constraint or something here
      return { type: "noop" };
    }
    if (isGroupBoundaryConstraint(constraint)) {
      return this.toColaGroupBoundary(constraint);
    }

    throw new Error("Constraint type not recognized");
  }

  /** Padding from each leaf's collision rectangle to this group's outer hull. */
  private groupMemberPadding(group: LayoutGroup): Map<number, number> {
    const name = this.collapsedGroupAliases.get(group.name) ?? group.name;
    const index = this.groupDefinitions.findIndex((g: { name: string }) => g.name === name);
    if (index < 0) throw new Error(`Missing rendered group: ${group.name}`);
    const collect = (groupIndex: number): Map<number, number> => {
      const definition = this.groupDefinitions[groupIndex];
      const padding = definition.padding;
      const members = new Map<number, number>();
      for (const leaf of definition.leaves) members.set(leaf, padding);
      for (const child of definition.groups) {
        for (const [leaf, innerPadding] of collect(child)) {
          members.set(leaf, Math.max(members.get(leaf) ?? 0, padding + innerPadding));
        }
      }
      return members;
    };
    return collect(index);
  }

  private toColaGroupBoundary(constraint: GroupBoundaryConstraint): ColaSeparationConstraint[] {
    const aMembers = this.groupMemberPadding(constraint.groupA);
    const bMembers = this.groupMemberPadding(constraint.groupB);
    const axis = constraint.side === 'left' || constraint.side === 'right' ? 'x' : 'y';
    const dimension = axis === 'x' ? 'width' : 'height';
    const reverse = constraint.side === 'right' || constraint.side === 'bottom';
    const result: ColaSeparationConstraint[] = [];
    for (const [a, aPadding] of aMembers) {
      for (const [b, bPadding] of bMembers) {
        const gap = (this.colaNodes[a][dimension] ?? 0) / 2
          + (this.colaNodes[b][dimension] ?? 0) / 2
          + aPadding + bPadding + constraint.minDistance;
        result.push({
          type: 'separation', axis, left: reverse ? b : a, right: reverse ? a : b, gap,
          // The existing transitive reduction checks reachability, not summed
          // distances. It must not discard the extra clearance for group hulls.
          groupBoundary: true,
        });
      }
    }
    return result;
  }

  /**
   * Tolerance (px) used when checking whether two prior positions already
   * satisfy a separation/alignment constraint. Matches the implicit slack
   * the WebCola solver uses when deciding constraints are "satisfied".
   */
  private static readonly CONSTRAINT_SATISFACTION_TOLERANCE = 0.5;

  /**
   * Lock decisions use the translated gaps, including group boundary clearance:
   *
   *   - `endpoints`: the node objects involved in this constraint, or
   *     `null` for constraints with no translated endpoints. Callers skip null.
   *   - `verdict`:
   *       'satisfied' — both endpoints have prior positions and they
   *                     satisfy the constraint (within tolerance). Both
   *                     can stay locked at fixed=1.
   *       'violated'  — both endpoints have prior positions but they do
   *                     NOT satisfy the constraint. Both must be unfixed
   *                     so the solver can repair.
   *       'unknown'   — at least one endpoint lacks a prior position
   *                     (newly added). Lock the seeded one, free the new
   *                     one.
   *
   * Each constraint uses one pair of node lookups by translated index.
   */
  private classifyConstraintForLocking(
    constraint: ColaConstraint
  ): {
    endpoints: [NodeWithMetadata, NodeWithMetadata];
    verdict: 'satisfied' | 'violated' | 'unknown';
  } | null {
    if (constraint.type !== 'separation') return null;
    const separation = constraint as ColaSeparationConstraint;
    const n1 = this.colaNodes[separation.left];
    const n2 = this.colaNodes[separation.right];
    if (!this.priorPositionMap.has(n1.id) || !this.priorPositionMap.has(n2.id)) {
      return { endpoints: [n1, n2], verdict: 'unknown' };
    }
    const actual = (n2[separation.axis] ?? 0) - (n1[separation.axis] ?? 0);
    const tol = WebColaLayout.CONSTRAINT_SATISFACTION_TOLERANCE;
    const satisfied = separation.equality
      ? Math.abs(actual - separation.gap) <= tol
      : actual + tol >= separation.gap;
    return { endpoints: [n1, n2], verdict: satisfied ? 'satisfied' : 'violated' };
  }

  /**
   * Post-pass over translated constraints (including group separations) that decides which endpoints
   * stay locked (fixed=1 from toColaNode) and which get unfixed.
   *
   * - lockUnconstrainedNodes=true (stability mode): per-constraint
   *   verdict drives the decision — satisfied keeps both locked,
   *   violated unfixes both, unknown unfixes only unseeded endpoints.
   * - lockUnconstrainedNodes=false: legacy behavior — any node touched
   *   by a constraint is unfixed. Short-circuited when no node was ever
   *   locked (priorPositionMap empty), since there's nothing to unfix.
   *
   * Cost: O(c) — one classification per constraint, each doing two
   * O(1) node-index lookups via nodeIndexMap.
   */
  private applyConstraintAwareLocking(constraints: ColaConstraint[]): void {
    const stability = this.lockUnconstrainedNodes;

    // Legacy mode + no nodes were locked → no-op.
    if (!stability && this.priorPositionMap.size === 0) return;

    for (const constraint of constraints) {
      const classified = this.classifyConstraintForLocking(constraint);
      if (!classified) continue;
      const [n1, n2] = classified.endpoints;

      if (!stability) {
        // Legacy: any constrained endpoint is freed.
        n1.fixed = 0;
        n2.fixed = 0;
        continue;
      }

      switch (classified.verdict) {
        case 'satisfied':
          // Prior positions honor the constraint — keep both locked.
          break;
        case 'violated':
          // Prior positions don't honor the constraint — free both so
          // the solver can repair.
          n1.fixed = 0;
          n2.fixed = 0;
          break;
        case 'unknown':
          // One side is a new node — free only the unseeded endpoint(s).
          if (!this.priorPositionMap.has(n1.id)) n1.fixed = 0;
          if (!this.priorPositionMap.has(n2.id)) n2.fixed = 0;
          break;
      }
    }
  }

  /**
   * Collapses identical nested groups to reduce jitter and constraint conflicts.
   * When multiple groups contain exactly the same set of nodes, they are merged
   * into a single group with combined labels.
   * 
   * This addresses WebCola jitter issues where identical groups create conflicting
   * constraints that cause the layout to oscillate rather than converge.
   * 
   * @param groups - Array of layout groups to deduplicate
   * @returns Array of groups with duplicates collapsed
   */
  /**
   * Maps each pre-collapse group name to its post-collapse (merged) name.
   * Merging joins ALL participant names with ' / ', so every member of a
   * merged set — including the first — is renamed. Edge fields stamped with
   * group names before the collapse (groupId, sourceGroupId, targetGroupId)
   * are remapped through this in the constructor.
   */
  private collapsedGroupAliases: Map<string, string> = new Map();

  private collapseIdenticalGroups(groups: LayoutGroup[]): LayoutGroup[] {
    if (groups.length === 0) return groups;

    // Group by node set signature (sorted node IDs as string)
    const groupsByNodes = new Map<string, LayoutGroup[]>();
    
    for (const group of groups) {
      // Create a canonical key from sorted node IDs
      const nodeKey = [...group.nodeIds].sort().join(',');
      if (!groupsByNodes.has(nodeKey)) {
        groupsByNodes.set(nodeKey, []);
      }
      groupsByNodes.get(nodeKey)!.push(group);
    }
    
    const collapsed: LayoutGroup[] = [];

    for (const duplicateGroups of groupsByNodes.values()) {
      if (duplicateGroups.length === 1) {
        // No duplicates, keep as-is
        collapsed.push(duplicateGroups[0]);
      } else {
        // Merge groups: preserve first group's structure but combine labels
        const mergedGroup: LayoutGroup = {
          ...duplicateGroups[0],
          // Combine names with separator for clarity
          name: duplicateGroups.map(g => g.name).join(' / '),
          // Show label if ANY of the duplicate groups wanted to show it
          showLabel: duplicateGroups.some(g => g.showLabel)
        };

        // Every participant is renamed by the merge; record aliases so
        // edge stamps that reference pre-merge names can be remapped.
        for (const dup of duplicateGroups) {
          this.collapsedGroupAliases.set(dup.name, mergedGroup.name);
        }

        collapsed.push(mergedGroup);
      }
    }
    
    return collapsed;
  }


  private determineGroups(groups: LayoutGroup[]): { leaves: number[], padding: number, name: string, groups: number[] }[] {

      // All groups (including overlapping ones) are passed to WebCola.
      // WebCola handles overlapping groups via its VPSC solver — shared nodes
      // appear as leaves in both groups, and both groups compute correct bounds.

      let groupsAsRecord: Record<string, string[]> = {};
      groups.forEach(group => {
        groupsAsRecord[group.name] = group.nodeIds;
      });

      let groupsAndSubgroups = this.determineGroupsAndSubgroups(groupsAsRecord);

      // `group` gets dynamic layout fields (keyNode/id/showLabel/labelTextStyle)
      // stamped onto it below, so it's typed loosely here.
      groupsAndSubgroups.forEach((group: any) => {
        // Every name here came from `groupsAsRecord`, which was built from
        // `groups`, so the lookup always hits. Guard anyway so a future change
        // to the subgrouping degrades to an unstamped group rather than a
        // TypeError deep inside the layout.
        const grp: LayoutGroup | undefined = groups.find(g => g.name === group.name);
        if (!grp) return;
        let keyNode = grp.keyNodeId;
        let keyIndex = this.getNodeIndex(keyNode);
        group['keyNode'] = keyIndex;
        group['id'] = grp.name;
        group['showLabel'] = grp.showLabel;
        // Match the renderer's label clearance before translating boundaries.
        if (grp.showLabel) group.padding = Math.max(group.padding, 20);
        // Group's own label styling (only color consumed today; size auto-fits).
        group['labelTextStyle'] = grp.labelTextStyle;
      });

      return groupsAndSubgroups;

    }


  // Returns true if group1 is a subgroup of group2
  private isSubGroup(group1: string[], group2: string[]) {
    return group1.every((node) => group2.includes(node));
  }



  private determineGroupsAndSubgroups(groupDefinitions: Record<string, string[]>) {
    let subgroups: Record<string, string[]> = {};


    Object.entries(groupDefinitions).forEach(([key1, value1]) => {
      Object.entries(groupDefinitions).forEach(([key2, value2]) => {

        const avoidContainmentCycle =
          key1 !== key2 // Group is not a subgroup of itself
          && (!subgroups[key2] || !subgroups[key2].includes(key1)) // Group is not a subgroup of a subgroup of itself
        const shouldAddSubgroup = avoidContainmentCycle && this.isSubGroup(value2, value1);


        if (shouldAddSubgroup) {

          if (subgroups[key1]) {
            subgroups[key1].push(key2);
          } else {
            subgroups[key1] = [key2];
          }
        }
      })
    });



    // TODO: But there may be groups that intersect with each other, but are not subgroups of each other.
    // WebCola struggles with this, so need to find a way to handle this.
    // Similarly, two webcola groups cannot share a subgroup.

    //Now modify groupDefinitions to be in the format that WebCola expects (ie indexed by node)

    const colaGroupsBeforeSubgrouping = Object.entries(groupDefinitions).map(([key, value]) => {


      // FIXME: The 'leaves' array is expected to contain node indices for WebCola, but in some cases it contains node objects instead.
      // This issue occurs when the mapping from node IDs to indices is not consistent, possibly due to changes in the node data structure or the getNodeIndex method.
      // To resolve this, ensure that 'leaves' always contains node indices before passing to WebCola. Refactor the code to handle cases where node objects are present, or update the mapping logic to guarantee indices.
      let leaves = value.map((nodeId) => this.getNodeIndex(nodeId));
      let name = key;
      // 12px keeps contained nodes visibly separated from the group boundary;
      // labelled groups still get bumped to GROUP_LABEL_PADDING in the renderer.
      const defaultPadding = 12;

      return { leaves, padding: defaultPadding, name };
    });

    const colaGroups = Object.entries(colaGroupsBeforeSubgrouping).map(([_key, value]) => {

      let leaves = value.leaves;
      let padding = value.padding;
      let name = value.name;


      // if the group has no subgroups, return it as is
      if (!subgroups[name]) {
        return { leaves, padding, name, groups: [] };
      }

      let groups = subgroups[name].map((subgroupName) => {
        // Get the index of the subgroup
        let subgroupIndex = colaGroupsBeforeSubgrouping.findIndex((group) => group.name === subgroupName);
        return subgroupIndex;
      });


      // Remove leaves in the subgroups from the leaves in the group
      groups.forEach((groupIndex) => {
        let group = colaGroupsBeforeSubgrouping[groupIndex];
        leaves = leaves.filter((leaf) => !group.leaves.includes(leaf));
      });

      return { leaves, padding, name, groups };
    });

    return colaGroups;
  }


  // Public getters for accessing layout data
  get nodes(): NodeWithMetadata[] {
    return this.colaNodes;
  }

  get links(): EdgeWithMetadata[] {
    return this.colaEdges;
  }

  get constraints(): ColaConstraint[] {
    return this.colaConstraints;
  }

  get groups(): any {
    // TODO: Make sure the leaves and 
    // groups are well defined here (i.e. not defined by index by object?)
    return this.groupDefinitions;
  }

  get conflictingNodes(): LayoutNode[] {
    const conflictingNodes: LayoutNode[] = [];
    const nodeById = new Map<string, LayoutNode>();
    if (this.instanceLayout) {
      for (const node of this.instanceLayout.nodes) nodeById.set(node.id, node);
    }
    this.conflictingConstraints.forEach(constraint => {
      if (isLeftConstraint(constraint)) {
        conflictingNodes.push(constraint.left);
        conflictingNodes.push(constraint.right);
      } else if (isTopConstraint(constraint)) {
        conflictingNodes.push(constraint.top);
        conflictingNodes.push(constraint.bottom);
      } else if (isAlignmentConstraint(constraint)) {
        conflictingNodes.push(constraint.node1);
        conflictingNodes.push(constraint.node2);
      } else if (isBoundingBoxConstraint(constraint)) {
        // The excluded node and the group members it conflicts with
        conflictingNodes.push(constraint.node);
        for (const id of constraint.group.nodeIds) {
          const member = nodeById.get(id);
          if (member) conflictingNodes.push(member);
        }
      } else if (isGroupBoundaryConstraint(constraint)) {
        // Members of both groups involved in the boundary conflict
        for (const id of constraint.groupA.nodeIds) {
          const member = nodeById.get(id);
          if (member) conflictingNodes.push(member);
        }
        for (const id of constraint.groupB.nodeIds) {
          const member = nodeById.get(id);
          if (member) conflictingNodes.push(member);
        }
      }
    });
    return conflictingNodes;
  }

  get overlappingNodes(): LayoutNode[] {
    return this.overlappingNodesData;
  }

  get reintroducedNodes(): LayoutNode[] {
    return this.reintroducedNodesData;
  }

  get overlappingGroups(): LayoutGroup[] {
    // Return groups from the instance layout that are marked as overlapping
    return (this.instanceLayout?.groups || []).filter(g => g.overlapping);
  }

  /**
   * Disposes of resources to help with garbage collection.
   * Clears the dagre_graph reference which can hold significant memory.
   */
  public dispose(): void {
    // Clear the dagre graph which can be memory-intensive
    if (this.dagre_graph) {
      this.dagre_graph = null;
    }
  }

  /**
   * Returns memory usage statistics for this layout.
   * Useful for monitoring and debugging memory consumption.
   * 
   * @returns Object containing memory-related metrics
   */
  public getMemoryStats(): {
    nodeCount: number;
    edgeCount: number;
    groupCount: number;
    constraintCount: number;
    hasDagreGraph: boolean;
  } {
    return {
      nodeCount: this.colaNodes?.length || 0,
      edgeCount: this.colaEdges?.length || 0,
      groupCount: this.groupDefinitions?.length || 0,
      constraintCount: this.colaConstraints?.length || 0,
      hasDagreGraph: !!this.dagre_graph
    };
  }
}

/**
 * WebColaTranslator - Main translator class for converting InstanceLayout to WebCola format
 */
export class WebColaTranslator {

  /**
   * Translate an InstanceLayout to WebColaLayout
   * @param instanceLayout The layout to translate
   * @param figWidth Optional figure width (default: 800)
   * @param figHeight Optional figure height (default: 800)
   * @param options Optional layout options including prior positions for temporal consistency
   * @returns Promise<WebColaLayout> The translated layout
   */
  async translate(
    instanceLayout: InstanceLayout, 
    figWidth: number = 800, 
    figHeight: number = 800,
    options?: WebColaLayoutOptions
  ): Promise<WebColaLayout> {
    return new WebColaLayout(instanceLayout, figHeight, figWidth, options);
  }
}
